import React, { useState } from 'react';
import { ArrowLeft, Plus, Sparkles, Lock, X, Check, Pencil, Trash2, Gift } from 'lucide-react';
import { CafeList, SharedStack, Store } from '../../types';
import { MOCK_CAFE_LISTS, MOCK_SHARED_STACKS, STORES } from '../../data/mockData';
import { PolaroidCard } from '../PolaroidCard';
import { useToast } from '../ToastContext';
interface FavoritePlacesScreenProps {
  onBack: () => void;
  onOpenList: (list: CafeList) => void;
  onMixAndShare: () => void;
}
type Tab = 'all' | 'mine' | 'shared';
const EMOJI_OPTIONS = ['☕', '🌸', '🌧️', '🎉', '❤️', '🌟', '📚', '🍕', '🥐', '🍔', '🍦', '🌮', '🎂', '🍷', '🌿', '🔥', '🌙', '✨'];
const storeById = (id: string): Store | undefined => STORES.find(s => s.id === id);
export function FavoritePlacesScreen({
  onBack,
  onOpenList,
  onMixAndShare
}: FavoritePlacesScreenProps) {
  const [tab, setTab] = useState<Tab>('all');
  const [lists, setLists] = useState<CafeList[]>(MOCK_CAFE_LISTS);
  const [shared] = useState<SharedStack[]>(MOCK_SHARED_STACKS);
  // Modal state — used for both create and edit
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftEmoji, setDraftEmoji] = useState('☕');
  const [draftSelectedCafes, setDraftSelectedCafes] = useState<string[]>([]);
  const [draftIsPrivate, setDraftIsPrivate] = useState(false);
  // Delete confirm
  const [deletingListId, setDeletingListId] = useState<string | null>(null);
  const {
    showToast
  } = useToast();
  const totalCafes = lists.reduce((sum, l) => sum + l.cafes.length, 0);
  const resetDraft = () => {
    setDraftName('');
    setDraftEmoji('☕');
    setDraftSelectedCafes([]);
    setDraftIsPrivate(false);
    setEditingListId(null);
  };
  const openCreate = () => {
    resetDraft();
    setModalMode('create');
  };
  const openEdit = (list: CafeList) => {
    setEditingListId(list.id);
    setDraftName(list.name);
    setDraftEmoji(list.emoji || '☕');
    setDraftSelectedCafes(list.cafes.map(c => c.storeId));
    setDraftIsPrivate(!!list.isPrivate);
    setModalMode('edit');
  };
  const closeModal = () => {
    setModalMode(null);
    resetDraft();
  };
  const toggleCafeSelection = (storeId: string) => {
    setDraftSelectedCafes(prev => prev.includes(storeId) ? prev.filter(id => id !== storeId) : [...prev, storeId]);
  };
  const handleSave = () => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      showToast('Give your list a name', 'error');
      return;
    }
    if (modalMode === 'create') {
      const newList: CafeList = {
        id: `list-${Date.now()}`,
        name: trimmed,
        emoji: draftEmoji,
        type: 'custom',
        isPrivate: draftIsPrivate,
        createdAt: new Date().toISOString(),
        cafes: draftSelectedCafes.map(storeId => ({
          storeId,
          addedAt: new Date().toISOString()
        }))
      };
      setLists(prev => [newList, ...prev]);
      showToast(`"${trimmed}" created!`, 'success');
      closeModal();
      setTimeout(() => onOpenList(newList), 200);
    } else if (modalMode === 'edit' && editingListId) {
      setLists(prev => prev.map(l => {
        if (l.id !== editingListId) return l;
        const existingByStoreId = new Map(l.cafes.map(c => [c.storeId, c]));
        const updatedCafes = draftSelectedCafes.map(storeId => existingByStoreId.get(storeId) || {
          storeId,
          addedAt: new Date().toISOString()
        });
        return {
          ...l,
          name: trimmed,
          emoji: draftEmoji,
          isPrivate: draftIsPrivate,
          cafes: updatedCafes
        };
      }));
      showToast('List updated', 'success');
      closeModal();
    }
  };
  const confirmDelete = () => {
    if (!deletingListId) return;
    const list = lists.find(l => l.id === deletingListId);
    setLists(prev => prev.filter(l => l.id !== deletingListId));
    showToast(`"${list?.name}" deleted`, 'info');
    setDeletingListId(null);
  };
  const renderListCard = (list: CafeList) => {
    const previewStores = list.cafes.slice(0, 3).map(c => storeById(c.storeId)).filter((s): s is Store => Boolean(s));
    const rotations = [-6, 2, 7];
    const isEditable = list.type !== 'been';
    return <div key={list.id} className="relative" data-id="element-757">
        <button onClick={() => onOpenList(list)} className="w-full bg-white border border-gray-200 rounded-2xl p-4 hover:border-gray-300 active:scale-[0.98] transition-all text-left" data-id="element-758">
          <div className="flex items-center justify-between mb-3 pr-16" data-id="element-759">
            <div className="flex items-center gap-2 min-w-0" data-id="element-760">
              <span className="text-xl flex-shrink-0" data-id="element-761">
                {list.emoji || '📌'}
              </span>
              <h3 className="font-bold text-gray-900 truncate" data-id="element-762">{list.name}</h3>
              {list.isPrivate && <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-763" />}
            </div>
            <span className="text-xs font-semibold text-gray-500 flex-shrink-0" data-id="element-764">
              {list.cafes.length} {list.cafes.length === 1 ? 'place' : 'places'}
            </span>
          </div>

          {/* Polaroid stack preview */}
          <div className="relative h-32 flex items-center justify-center" data-id="element-765">
            {previewStores.length === 0 ? <div className="text-sm text-gray-400 italic" data-id="element-766">
                Empty list — add cafes
              </div> : previewStores.map((store, i) => <div key={store.id} className="absolute" style={{
            left: `${30 + i * 14}%`,
            transform: `translateX(-50%)`,
            zIndex: i
          }} data-id="element-767">
                  <PolaroidCard store={store} rotation={rotations[i]} size="sm" data-id="element-768" />
                </div>)}
          </div>

          {list.type === 'been' && <div className="mt-2 text-xs text-gray-500" data-id="element-769">
              Auto-updated from your check-ins
            </div>}
        </button>

        {/* Floating edit/delete sticker actions */}
        {isEditable && <div className="absolute top-3 right-3 flex gap-1.5 z-10" data-id="element-770">
            <button onClick={e => {
          e.stopPropagation();
          openEdit(list);
        }} aria-label="Edit list" className="w-8 h-8 bg-[#FFFC00] hover:bg-[#F7C300] rounded-full flex items-center justify-center shadow-sm active:scale-90 transition-all" style={{
          transform: 'rotate(-6deg)'
        }} data-id="element-771">
              <Pencil size={13} className="text-black" strokeWidth={2.5} data-id="element-772" />
            </button>
            <button onClick={e => {
          e.stopPropagation();
          setDeletingListId(list.id);
        }} aria-label="Delete list" className="w-8 h-8 bg-white hover:bg-red-50 border border-gray-200 rounded-full flex items-center justify-center shadow-sm active:scale-90 transition-all" style={{
          transform: 'rotate(5deg)'
        }} data-id="element-773">
              <Trash2 size={13} className="text-gray-500" strokeWidth={2.5} data-id="element-774" />
            </button>
          </div>}
      </div>;
  };
  const renderSharedCard = (stack: SharedStack) => {
    const previewStores = stack.cafeIds.slice(0, 3).map(id => storeById(id)).filter((s): s is Store => Boolean(s));
    const rotations = [-8, 3, 9];
    return <div key={stack.id} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm" data-id="element-775">
        <div className="flex items-center justify-between mb-2" data-id="element-776">
          <div data-id="element-777">
            <h3 className="font-bold text-gray-900" data-id="element-778">{stack.title}</h3>
            <p className="text-xs text-gray-500" data-id="element-779">
              From {stack.fromUser} · {stack.cafeIds.length} places
            </p>
          </div>
        </div>
        <div className="relative h-32 flex items-center justify-center mb-3" data-id="element-780">
          {previewStores.map((store, i) => <div key={store.id} className="absolute" style={{
          left: `${30 + i * 14}%`,
          transform: `translateX(-50%)`,
          zIndex: i
        }} data-id="element-781">
              <PolaroidCard store={store} rotation={rotations[i]} size="sm" data-id="element-782" />
            </div>)}
        </div>
        <button onClick={() => showToast('Saved to your lists!', 'success')} className="w-full py-2 bg-[#FFFC00] text-black rounded-lg text-sm font-bold border border-black/10 hover:bg-[#F7C300] active:scale-95 transition-all" data-id="element-783">
          Save to my lists
        </button>
      </div>;
  };
  const renderSharedSection = (withBand: boolean) => <div className={withBand ? 'relative -mx-4 px-4 pt-8 pb-5 bg-gradient-to-b from-yellow-50/80 to-white border-t-2 border-dashed border-[#FFFC00] mt-6' : 'relative'} data-id="element-784">
      {withBand && <>
          {/* Yellow tape stickers on top edges */}
          <div className="absolute -top-3 left-6 w-16 h-5 bg-[#FFFC00] z-10" style={{
        transform: 'rotate(-4deg)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
      }} data-id="element-785" />
          <div className="absolute -top-3 right-10 w-12 h-5 bg-[#FFFC00] z-10" style={{
        transform: 'rotate(6deg)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
      }} data-id="element-786" />
          <div className="flex items-center gap-2.5 mb-4" data-id="element-787">
            <div className="w-9 h-9 bg-[#FFFC00] rounded-full flex items-center justify-center shadow-sm" data-id="element-788">
              <Gift size={18} className="text-black" strokeWidth={2.5} data-id="element-789" />
            </div>
            <div data-id="element-790">
              <h2 className="text-xl font-bold text-gray-900 leading-tight" style={{
            fontFamily: "'Caveat', cursive"
          }} data-id="element-791">
                Shared with you
              </h2>
              <p className="text-[11px] text-gray-500" data-id="element-792">
                Stacks your friends curated
              </p>
            </div>
          </div>
        </>}
      <div className="space-y-3" data-id="element-793">{shared.map(renderSharedCard)}</div>
    </div>;
  return <div className="flex flex-col h-full bg-white pb-24" data-id="element-794">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100" data-id="element-795">
        <div className="flex items-center gap-3 px-4 py-4" data-id="element-796">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-797">
            <ArrowLeft size={22} data-id="element-798" />
          </button>
          <div className="flex-1" data-id="element-799">
            <h1 className="text-lg font-bold text-gray-900" data-id="element-800">Favorite Places</h1>
            <p className="text-xs text-gray-500" data-id="element-801">
              {lists.length} lists · {totalCafes} cafes saved
            </p>
          </div>
        </div>

        {/* CTAs */}
        <div className="px-4 pb-3 grid grid-cols-2 gap-2" data-id="element-802">
          <button onClick={onMixAndShare} className="flex items-center justify-center gap-2 py-3 bg-[#FFFC00] text-black rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-sm" data-id="element-803">
            <Sparkles size={16} data-id="element-804" />
            Mix & Share
          </button>
          <button onClick={openCreate} className="flex items-center justify-center gap-2 py-3 bg-black text-white rounded-xl font-bold text-sm active:scale-95 transition-transform" data-id="element-805">
            <Plus size={16} data-id="element-806" />
            New List
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-4 gap-1 border-b border-gray-100" data-id="element-807">
          {(['all', 'mine', 'shared'] as Tab[]).map(t => <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-semibold capitalize relative transition-colors ${tab === t ? 'text-black' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-808">
              {t === 'mine' ? 'My Lists' : t}
              {tab === t && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-black rounded-full" data-id="element-809" />}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 flex-1" data-id="element-810">
        {(tab === 'all' || tab === 'mine') && <div className="space-y-3" data-id="element-811">{lists.map(renderListCard)}</div>}

        {tab === 'all' && shared.length > 0 && renderSharedSection(true)}

        {tab === 'shared' && <>
            {shared.length > 0 ? renderSharedSection(false) : <div className="text-center py-12 text-gray-400" data-id="element-812">
                <Gift size={32} className="mx-auto mb-2 opacity-50" data-id="element-813" />
                <p className="text-sm" data-id="element-814">No shared stacks yet</p>
              </div>}
          </>}
      </div>

      {/* Create / Edit Bottom Sheet */}
      {modalMode && <div className="fixed inset-0 z-50 flex items-end justify-center animate-in fade-in duration-200" onClick={closeModal} data-id="element-815">
          <div className="absolute inset-0 bg-black/50" data-id="element-816" />
          <div className="relative w-full max-w-md bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-817">
            <div className="flex justify-center pt-3 pb-1" data-id="element-818">
              <div className="w-10 h-1 bg-gray-200 rounded-full" data-id="element-819" />
            </div>

            <div className="flex items-center justify-between px-5 pt-2 pb-4 border-b border-gray-100" data-id="element-820">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-821">
                {modalMode === 'create' ? 'New List' : 'Edit List'}
              </h2>
              <button onClick={closeModal} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-id="element-822">
                <X size={18} className="text-gray-600" data-id="element-823" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-5 space-y-6" data-id="element-824">
              {/* Live preview */}
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-yellow-50 to-pink-50 rounded-2xl border border-gray-100" data-id="element-825">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-3xl shadow-sm" data-id="element-826">
                  {draftEmoji}
                </div>
                <div className="flex-1 min-w-0" data-id="element-827">
                  <p className="font-bold text-gray-900 truncate" style={{
                fontFamily: "'Caveat', cursive",
                fontSize: '1.4rem',
                lineHeight: 1.1
              }} data-id="element-828">
                    {draftName.trim() || 'Untitled list'}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-829">
                    {draftSelectedCafes.length}{' '}
                    {draftSelectedCafes.length === 1 ? 'place' : 'places'}
                    {draftIsPrivate ? ' · private' : ''}
                  </p>
                </div>
              </div>

              {/* Name */}
              <div data-id="element-830">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 block" data-id="element-831">
                  List name
                </label>
                <input autoFocus type="text" value={draftName} onChange={e => setDraftName(e.target.value)} maxLength={30} placeholder="e.g. Sunday breakfast spots" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none focus:ring-2 focus:ring-black/10 text-sm" data-id="element-832" />
                <p className="text-[10px] text-gray-400 mt-1 text-right" data-id="element-833">
                  {draftName.length}/30
                </p>
              </div>

              {/* Emoji picker */}
              <div data-id="element-834">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 block" data-id="element-835">
                  Pick an icon
                </label>
                <div className="grid grid-cols-6 gap-2" data-id="element-836">
                  {EMOJI_OPTIONS.map(emoji => <button key={emoji} onClick={() => setDraftEmoji(emoji)} className={`aspect-square rounded-xl text-2xl flex items-center justify-center transition-all active:scale-90 ${draftEmoji === emoji ? 'bg-[#FFFC00] ring-2 ring-black' : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-837">
                      {emoji}
                    </button>)}
                </div>
              </div>

              {/* Privacy toggle */}
              <button onClick={() => setDraftIsPrivate(!draftIsPrivate)} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-838">
                <div className="flex items-center gap-3" data-id="element-839">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center" data-id="element-840">
                    <Lock size={14} className="text-gray-700" data-id="element-841" />
                  </div>
                  <div className="text-left" data-id="element-842">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-843">
                      Keep private
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-844">
                      Only you can see this list
                    </p>
                  </div>
                </div>
                <div className={`w-10 h-6 rounded-full relative transition-colors ${draftIsPrivate ? 'bg-black' : 'bg-gray-300'}`} data-id="element-845">
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${draftIsPrivate ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-846" />
                </div>
              </button>

              {/* Cafes */}
              <div data-id="element-847">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 block" data-id="element-848">
                  {modalMode === 'edit' ? 'Cafes in this list' : 'Add cafes (optional)'}
                </label>
                <p className="text-[11px] text-gray-500 mb-3" data-id="element-849">
                  Tap to add or remove. You can also manage cafes from the list
                  page.
                </p>
                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1" data-id="element-850">
                  {STORES.slice(0, 12).map(store => {
                const isSelected = draftSelectedCafes.includes(store.id);
                return <button key={store.id} onClick={() => toggleCafeSelection(store.id)} className={`relative rounded-xl overflow-hidden aspect-square transition-all active:scale-95 ${isSelected ? 'ring-4 ring-[#FFFC00] ring-offset-1' : 'ring-1 ring-gray-200'}`} data-id="element-851">
                        <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-852" />
                        <div className={`absolute inset-0 transition-colors ${isSelected ? 'bg-black/30' : 'bg-black/0'}`} data-id="element-853" />
                        {isSelected && <div className="absolute top-1 right-1 w-5 h-5 bg-[#FFFC00] rounded-full flex items-center justify-center shadow-md" data-id="element-854">
                            <Check size={11} className="text-black" strokeWidth={3} data-id="element-855" />
                          </div>}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1" data-id="element-856">
                          <p className="text-[9px] font-semibold text-white truncate" data-id="element-857">
                            {store.name}
                          </p>
                        </div>
                      </button>;
              })}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 p-4 grid grid-cols-2 gap-2 bg-white" data-id="element-858">
              <button onClick={closeModal} className="py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm active:scale-95 transition-transform" data-id="element-859">
                Cancel
              </button>
              <button onClick={handleSave} disabled={!draftName.trim()} className="py-3 bg-black text-[#FFFC00] rounded-xl font-bold text-sm active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100" data-id="element-860">
                {modalMode === 'create' ? 'Create List' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deletingListId && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-200" onClick={() => setDeletingListId(null)} data-id="element-861">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl animate-in slide-in-from-bottom duration-300 relative" onClick={e => e.stopPropagation()} data-id="element-862">
            <div className="absolute -top-2 left-1/2 w-16 h-5 bg-[#FFFC00] shadow-sm" style={{
          transform: 'translateX(-50%) rotate(-3deg)'
        }} data-id="element-863" />
            <div className="text-center pt-2" data-id="element-864">
              <div className="w-14 h-14 bg-red-50 rounded-full mx-auto mb-4 flex items-center justify-center" data-id="element-865">
                <Trash2 size={24} className="text-red-500" data-id="element-866" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-867">
                Delete this list?
              </h3>
              <p className="text-sm text-gray-500 mb-5" data-id="element-868">
                The list "{lists.find(l => l.id === deletingListId)?.name}"
                will be removed. The cafes themselves stay available everywhere
                else.
              </p>
              <div className="grid grid-cols-2 gap-2" data-id="element-869">
                <button onClick={() => setDeletingListId(null)} className="py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm active:scale-95 transition-transform" data-id="element-870">
                  Keep it
                </button>
                <button onClick={confirmDelete} className="py-3 bg-red-500 text-white rounded-xl font-bold text-sm active:scale-95 transition-transform hover:bg-red-600" data-id="element-871">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}